/** @format */

import React, { FC, forwardRef, useEffect, useImperativeHandle } from 'react'
import { Graph, Shape } from '@antv/x6'
import styled from 'styled-components'
import { useWindowSize, useDebounce } from 'react-use'
import { GraphX6Props } from 'src/components/GraphX6/types'
import { Stencil } from '@antv/x6-plugin-stencil'
import { Transform } from '@antv/x6-plugin-transform'
import { Selection } from '@antv/x6-plugin-selection'
import { Snapline } from '@antv/x6-plugin-snapline'
import { Keyboard } from '@antv/x6-plugin-keyboard'
import { Clipboard } from '@antv/x6-plugin-clipboard'
import { History } from '@antv/x6-plugin-history'
import { Export } from '@antv/x6-plugin-export'
import { specialAttrs, registerNode } from 'src/components/GraphX6/container'

const GraphX6: FC<GraphX6Props> = forwardRef(
  ({ className, editable = false, config = {}, onGraphReady, onHistoryChange }, ref) => {
    const graphRef = React.useRef<Graph | null>(null)
    const containerRef = React.useRef<HTMLDivElement>(null)
    const stencilRef = React.useRef<HTMLDivElement>(null)
    // 撤销/重做

    const { width: winWidth, height: winHeight } = useWindowSize()

    // 初始化
    useEffect(() => {
      if (graphRef?.current || !containerRef?.current) return

      // #region 初始化画布
      const graph = new Graph({
        container: document.getElementById('app-content'),
        autoResize: true,
        interacting: {
          nodeMovable: editable
        },
        // 允许画布拖拽平移
        panning: {
          enabled: editable,
          eventTypes: ['leftMouseDown', 'rightMouseDown', 'mouseWheel'], //触发画布拖拽的行为
          modifiers: 'ctrl'
        },
        grid: true,
        mousewheel: {
          enabled: true,
          zoomAtMousePosition: true,
          modifiers: 'ctrl',
          minScale: 0.5,
          maxScale: 3
        },
        connecting: {
          router: 'manhattan',
          connector: {
            name: 'rounded',
            args: {
              radius: 8
            }
          },
          anchor: 'center',
          connectionPoint: 'anchor',
          allowBlank: false,
          snap: {
            radius: 20
          },
          createEdge() {
            return new Shape.Edge({
              attrs: {
                line: {
                  stroke: '#A2B1C3',
                  strokeWidth: 2,
                  targetMarker: {
                    name: 'block',
                    width: 12,
                    height: 8
                  }
                }
              },
              zIndex: 0,
              selectable: true
            })
          },
          validateConnection({ targetMagnet }) {
            return !!targetMagnet
          }
        },
        highlighting: {
          magnetAdsorbed: {
            name: 'stroke',
            args: {
              attrs: {
                fill: '#5F95FF',
                stroke: '#5F95FF'
              }
            }
          }
        }
      })

      // 注册自定义节点
      registerNode()

      // #region 使用插件
      graph
        .use(
          // 缩放
          new Transform({
            resizing: true,
            rotating: false
          })
        )
        .use(
          new Selection({
            multiple: true,
            enabled: true,
            rubberband: true,
            showNodeSelectionBox: true,
            pointerEvents: 'none'
          })
        )
        .use(new Snapline())
        .use(
          new Keyboard({
            global: editable
          })
        )
        .use(new Clipboard())
        .use(new History())
        .use(new Export())

      if (editable && stencilRef?.current) {
        // 初始化 stencil
        const stencil = new Stencil({
          title: '流程图',
          target: graph,
          stencilGraphWidth: 180,
          stencilGraphHeight: 0,
          groups: [
            {
              title: '基础流程图',
              name: 'group1'
            }
          ],
          layoutOptions: {
            columns: 1,
            columnWidth: 180,
            rowHeight: 65
          }
        })
        document.getElementById('app-stencil').appendChild(stencil.container)
        // 创建 stencil 节点（确保在节点注册之后）
        const r1 = graph.createNode({
          shape: 'custom-rect',
          label: '开始',
          attrs: specialAttrs.nodes.START,
          data: {
            type: 'START'
          }
        })
        const r2 = graph.createNode({
          shape: 'custom-polygon',
          attrs: specialAttrs.nodes.AUTOTASK_DISPLAY,
          label: '数据处理',
          data: {
            type: 'AUTOTASK_DISPLAY'
          }
        })
        const r3 = graph.createNode({
          shape: 'custom-polygon',
          attrs: specialAttrs.nodes.GATEWAY,
          label: '验证',
          data: {
            type: 'GATEWAY'
          }
        })
        const r4 = graph.createNode({
          shape: 'custom-rect',
          label: '子场景',
          attrs: specialAttrs.nodes.AUTOTASK_SUBSCENE,
          data: {
            type: 'AUTOTASK_SUBSCENE'
          }
        })
        const r5 = graph.createNode({
          shape: 'custom-polygon',
          label: '会签',
          attrs: specialAttrs.nodes.PARALLEL_GATEWAY,
          data: {
            type: 'PARALLEL_GATEWAY'
          }
        })
        const r6 = graph.createNode({
          shape: 'custom-rect',
          label: '结束',
          attrs: specialAttrs.nodes.END,
          data: {
            type: 'END'
          }
        })
        const r7 = graph.createNode({
          shape: 'custom-circle',
          label: '定时器',
          data: {
            type: 'AUTOTASK_TIMER'
          }
        })
        const r8 = graph.createNode({
          shape: 'custom-polygon',
          label: '人工介入',

          attrs: {
            ...specialAttrs.nodes.USERTASK
          },
          data: {
            type: 'USERTASK'
          }
        })
        stencil.load([r1, r2, r3, r4, r5, r6, r7, r8], 'group1')
        // 控制连接桩显示/隐藏
        const showPorts = (ports: NodeListOf<SVGElement>, show: boolean) => {
          for (let i = 0, len = ports.length; i < len; i += 1) {
            ports[i].style.visibility = show ? 'visible' : 'hidden'
          }
        }
        graph.on('node:mouseenter', () => {
          const container = document.getElementById('app-content')
          const ports = container.querySelectorAll('.x6-port-body') as NodeListOf<SVGElement>
          showPorts(ports, true)
        })
        graph.on('node:mouseleave', () => {
          const container = document.getElementById('app-content')
          const ports = container.querySelectorAll('.x6-port-body') as NodeListOf<SVGElement>
          showPorts(ports, false)
        })

        // delete
        graph.bindKey('backspace', () => {
          const cells = graph.getSelectedCells()
          if (cells.length) {
            graph.removeCells(cells)
          }
        })

        //undo redo
        graph.bindKey(['meta+z', 'ctrl+z'], () => {
          if (graph.canUndo()) {
            graph.undo()
          }
          return false
        })
        graph.bindKey(['meta+shift+z', 'ctrl+shift+z'], () => {
          if (graph.canRedo()) {
            graph.redo()
          }
          return false
        })

        // 撤销/重做
        if (onHistoryChange) {
          graph.on('history:change', () => {
            onHistoryChange(graph.canRedo(), graph.canUndo())
          })
        }
      }

      graphRef.current = graph

      // 通知父组件图形已准备就绪
      if (onGraphReady) {
        onGraphReady(graph)
      }
    }, [containerRef?.current, stencilRef?.current, config])

    // 窗口大小变化时调整画布大小
    useDebounce(
      () => {
        if (containerRef?.current && graphRef?.current) {
          // const { offsetWidth, offsetHeight } = containerRef.current
          graphRef.current.centerContent()
        }
      },
      200,
      [winWidth, winHeight]
    )

    // 卸载
    useEffect(() => {
      return () => {
        console.log('destroy')
        graphRef.current?.dispose()
        graphRef.current = null
      }
    }, [])

    useImperativeHandle(ref, () => {
      return {
        graph: graphRef?.current
      }
    })

    return (
      <div className={className} style={{ width: '100%', height: '100%' }}>
        {editable ? <div ref={stencilRef} className="app-stencil" id="app-stencil" /> : null}
        <div ref={containerRef} className="app-content" id="app-content" />
      </div>
    )
  }
)

export default styled(GraphX6)`
  display: flex;
  padding: 0;
  font-family: sans-serif;

  .app-stencil {
    position: relative;
    width: 200px;
    border-right: 1px solid #f0f0f0;
  }

  .app-content {
    flex: 1;
  }
  .x6-node {
    cursor: pointer;
  }

  .x6-edge {
    cursor: pointer;

    &.x6-edge-selected {
      path {
        stroke: #1890ff !important;
        stroke-width: 3px !important;
        stroke-dasharray: 0 !important;
      }
    }

    &:hover {
      path {
        stroke: #5f95ff;
        stroke-width: 2.5px;
      }
    }
  }
`
